// --------------------------------------------------------------
// Created On: 2025-04-01
// Author: Dimitra Weinstein
//
// Last Modified: 2025-11-06
// Modified By: Zachary Thomas
//
// Copyright 2025 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { Fragment, useState } from "react";
import { API, MAX_IMPELLER_ANGLE, MAX_IMPELLER_TRIM, MAX_ITEM_QUANTITY } from "../../constants/miscellaneous";
import useApi from "../../hooks/useApi";
import getApiError from "../../utilities/api/getApiError";
import QuickOrderList from "./QuickOrderList/QuickOrderList";
import apiRequest from "../../utilities/api/apiRequest";
import deepCopy from "../../utilities/deepCopy";
import stringIsInteger from "../../utilities/stringIsInteger";
import QuickOrderForm from "./QuickOrderForm/QuickOrderForm";
import Toast from "../../components/Toast/Toast";
import stringIsValidNumber from "../../utilities/stringIsValidNumber";
import useReduxSelector from "src/hooks/useReduxSelector";
import useRefreshCart from "../../hooks/useRefreshCart";
import { useNavigate } from "react-router-dom";
import ImportCsvButton from "./ImportCsvButton/ImportCsvButton";
import ImportCsvInfoModal from "../QuickOrderPage/ImportCsvInfoModal/ImportCsvInfoModal";
import HelpImportCsv from "../../components/HelpImportCsv/HelpImportCsv";
import styles from "./QuickOrderPage.module.scss";

// Displays the Quick Order page, which allows users to quickly order parts by
// entering part numbers or uploading a CSV file.
export default function QuickOrderPage(): Component {
  const [loading, setLoading] = useState<boolean>(false);
  const [partFound, setPartFound] = useState<boolean>(true);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [inputValue, setInputValue] = useState<string>("");
  const [paintColors, setPaintColors] = useState<PaintColor[]>([]);
  const [defaultPaintColorId, setDefaultPaintColorId] = useState<number | null>(null);
  const [searchComplete, setSearchComplete] = useState<boolean>(false);
  const [partsList, setPartsList] = useState<Part[]>([]);
  const [cartQuantityCounter, setCartQuantityCounter] = useState<number>(0);
  const [successMessage, setSuccessMessage] = useState<string>("");
  const [showHelpModal, setShowHelpModal] = useState<boolean>(false);
  const currentUser = useReduxSelector((state) => state.currentUser);
  const navigate = useNavigate();

  // Refresh the Redux store's cart quantity whenever an item is added to the cart.
  useRefreshCart(() => {
    return cartQuantityCounter > 0;
  }, [cartQuantityCounter]);

  // Retrieve paint colors from database.
  useApi(
    () => {
      return true;
    },
    {
      url: `${API}/paint-color`,
      method: "GET",
    },
    async (response: Response, responseBody: PaintColorResponseBody) => {
      if (response.ok && responseBody) {
        setPaintColors(responseBody.paintColors);
      } else {
        console.log(await getApiError(response, "Error loading paint colors."));
      }
    },
    []
  );

  // Get company default paint color.
  useApi(
    () => {
      return true;
    },
    {
      url: `${API}/company/${currentUser.companyId}/preference`,
      method: "GET",
    },
    async (response: Response, responseBody: CompanyPreferenceResponseBody) => {
      if (response.ok && responseBody && responseBody.defaultPaintColor !== null) {
        setErrorMessage("");
        setDefaultPaintColorId(responseBody.defaultPaintColor.paintColorId);
      } else if (!response.ok) {
        setErrorMessage(await getApiError(response, "Unable to retrieve company preference setting."));
      }
    },
    []
  );

  // Perform part number search.
  async function performSearch(): Promise<void> {
    const requestBody = {
      type: "part",
      input: `${inputValue.toUpperCase()}`,
    };
    setLoading(true);
    setErrorMessage("");
    setSearchComplete(false);
    const [response, responseBody] = await apiRequest(`${API}/search`, "POST", requestBody);
    if (response.ok && responseBody) {
      const [response, responseBody] = (await apiRequest(`${API}/part/${inputValue.toUpperCase()}`, "GET", null)) as [
        Response,
        ResponseBody
      ];
      if (response.ok && responseBody) {
        setPartFound(true);
        setErrorMessage("");
        setInputValue("");
        const foundPart: Part = {
          partId: responseBody.partId,
          partNumber: responseBody.partNumber,
          description: responseBody.description,
          quantity: "",
          unitName: responseBody.unitName,
          price: responseBody.price,
          companyDiscount: responseBody.companyDiscount,
          weight: responseBody.weight,
          paintColorId: responseBody.isPaintable ? defaultPaintColorId : null,
          partNumberIsInvalid: false,
          invalidPartNumberMessage: "",
          isSellable: responseBody.isSellable,
          isPaintable: responseBody.isPaintable,
          isImpeller: responseBody.isImpeller,
          isFullTrim: false,
          impellerTrimOption: "",
          impellerTrimValue: "",
          impellerAngle: "",
        };
        // If the part is already in the list, increase the quantity by 1.
        // Otherwise, add the part to the list.
        if (partsList.length > 0) {
          const partsListDeepCopy = deepCopy(partsList);
          const foundPartIndex = getPartIndex(foundPart.partId, partsListDeepCopy);
          if (foundPartIndex !== -1 && !foundPart.isImpeller && !foundPart.isPaintable && foundPart.unitName === "EA") {
            partsListDeepCopy[foundPartIndex].quantity = (
              Number(partsListDeepCopy[foundPartIndex].quantity) + 1
            ).toString();
            setPartsList(partsListDeepCopy);
          } else {
            setPartsList([...partsList, foundPart]);
          }
        } else {
          setPartsList([...partsList, foundPart]);
        }
        setSearchComplete(true);
        setLoading(false);
      } else {
        setPartFound(false);
        setErrorMessage(await getApiError(response, "Error loading part."));
        setLoading(false);
      }
      setPartFound(true);
      setInputValue("");
      setErrorMessage("");
      setSearchComplete(true);
      setLoading(false);
    } else {
      setPartFound(false);
      setErrorMessage("Invalid part number or Part Not Found. Please check the part number and try again.");
      setLoading(false);
    }
  }

  // Calculate trim option and value.
  function calculateTrimOptionAndValue(trimValue: string | null): [string, string] {
    let calculatedTrimOptionAndValue: [string, string] = ["", ""];
    switch (trimValue) {
      case "full-trim":
        calculatedTrimOptionAndValue = ["full-trim", "0.0"];
        break;
      case "0.0":
        calculatedTrimOptionAndValue = ["full-trim", "0.0"];
        break;
      case "custom":
        calculatedTrimOptionAndValue = ["custom", trimValue];
        break;
      default:
        calculatedTrimOptionAndValue = ["", ""];
        break;
    }
    return calculatedTrimOptionAndValue;
  }

  // Process uploaded parts.
  async function processUploadedParts(uploadedPartsList: Part[]) {
    const processedParts: ProcessedPart[] = [];
    for (const part of uploadedPartsList) {
      const processedPart = await retrieveUploadedPartInformation(part);
      processedParts.push(processedPart);
    }
    setPartsList(processedParts);
  }

  // Validate uploaded part number and retrieve additional details.
  async function retrieveUploadedPartInformation(part: Part): Promise<ProcessedPart> {
    const requestBody = {
      type: "part",
      input: `${part.partNumber.toUpperCase()}`,
    };
    setLoading(true);
    setErrorMessage("");
    setSearchComplete(false);
    const [response, responseBody] = await apiRequest(`${API}/search`, "POST", requestBody);
    if (response.ok && responseBody) {
      const [response, responseBody] = (await apiRequest(
        `${API}/part/${part.partNumber.toUpperCase()}`,
        "GET",
        null
      )) as [Response, ResponseBody];
      if (response.ok && responseBody) {
        setPartFound(true);
        setErrorMessage("");
        setInputValue("");
        part = {
          partId: responseBody.partId,
          partNumber: part.partNumber,
          description: responseBody.description,
          quantity: part.quantity,
          unitName: responseBody.unitName,
          price: responseBody.price,
          companyDiscount: responseBody.companyDiscount,
          weight: responseBody.weight,
          paintColorId: responseBody.isPaintable
            ? validatePaintColor(part.paintColorId)
              ? part.paintColorId
              : null
            : null,
          partNumberIsInvalid: false,
          invalidPartNumberMessage: "",
          isSellable: responseBody.isSellable,
          isPaintable: responseBody.isPaintable,
          isImpeller: responseBody.isImpeller,
          isFullTrim:
            part.impellerTrimValue !== null && part.impellerTrimValue.toLowerCase() === "full trim" ? true : false,
          impellerTrimOption: calculateTrimOptionAndValue(part.impellerTrimOption)[0],
          impellerTrimValue: part.impellerTrimValue,
          impellerAngle: responseBody.isImpeller && part.impellerAngle !== "" ? part.impellerAngle : "",
        };
        setSearchComplete(true);
        setErrorMessage("");
        setLoading(false);
      }
    } else {
      part = {
        partId: 0,
        partNumber: part.partNumber,
        description: "",
        quantity: "",
        unitName: "",
        price: 0.0,
        companyDiscount: 0.0,
        weight: null,
        paintColorId: null,
        partNumberIsInvalid: true,
        invalidPartNumberMessage: "Part number invalid. Please check the part number and try again.",
        isSellable: false,
        isPaintable: false,
        isImpeller: false,
        isFullTrim: false,
        impellerTrimOption: "",
        impellerTrimValue: "",
        impellerAngle: "",
      };
      setLoading(false);
    }
    setPartFound(true);
    setSearchComplete(true);
    setErrorMessage("");
    setInputValue("");
    setLoading(false);
    return part;
  }

  // Get the index of the given part id in the parts list.
  function getPartIndex(partId: number | null, partsListDeepCopy: Part[]) {
    return partsListDeepCopy.findIndex((part) => part.partId === partId);
  }

  // Delete part from parts list.
  function handleDeletePart(lineItemNumber: number) {
    const partsListDeepCopy = deepCopy(partsList);
    const partIndex = lineItemNumber - 1;
    partsListDeepCopy.splice(partIndex, 1);
    setPartsList(partsListDeepCopy);
  }

  // Change paint color of part.
  function handlePaintColorChange(index: number, colorId: number | null) {
    const partsListDeepCopy = deepCopy(partsList);
    partsListDeepCopy[index].paintColorId = colorId;
    setPartsList(partsListDeepCopy);
  }

  // Change part number of part.
  function handlePartNumberChange(index: number, newPartNumber: string) {
    const currentPartNumber = partsList[index].partNumber;
    // If the part number is the same, do nothing.
    if (currentPartNumber === newPartNumber) {
      return;
    } else {
      const partsListDeepCopy = deepCopy(partsList);
      partsListDeepCopy[index].partNumber = newPartNumber;
      setPartsList(partsListDeepCopy);
      setErrorMessage("");
    }
  }

  // Perform part number search on change within part number input.
  async function performPartNumberSearch(index: number, newPartNumber: string) {
    const partsListDeepCopy = deepCopy(partsList);
    // Make api call to get part information and validate part number.
    const requestBody = {
      type: "part",
      input: `${newPartNumber.toUpperCase()}`,
    };
    setLoading(true);
    setErrorMessage("");
    setSearchComplete(false);
    const [response, responseBody] = await apiRequest(`${API}/search`, "POST", requestBody);
    if (response.ok && responseBody) {
      const [response, responseBody] = (await apiRequest(
        `${API}/part/${newPartNumber.toUpperCase()}`,
        "GET",
        null
      )) as [Response, ResponseBody];
      if (response.ok && responseBody) {
        partsListDeepCopy[index].partNumber = newPartNumber;
        partsListDeepCopy[index].partId = responseBody.partId;
        partsListDeepCopy[index].description = responseBody.description;
        partsListDeepCopy[index].quantity =
          partsListDeepCopy[index].quantity !== "" ? partsListDeepCopy[index].quantity : "";
        partsListDeepCopy[index].unitName = responseBody.unitName;
        partsListDeepCopy[index].price = responseBody.price;
        partsListDeepCopy[index].companyDiscount = responseBody.companyDiscount;
        partsListDeepCopy[index].weight = responseBody.weight;
        partsListDeepCopy[index].isPaintable = responseBody.isPaintable;
        partsListDeepCopy[index].isImpeller = responseBody.isImpeller;
        partsListDeepCopy[index].isSellable = responseBody.isSellable;
        partsListDeepCopy[index].paintColorId = responseBody.isPaintable
          ? partsListDeepCopy[index].paintColorId !== null
            ? partsListDeepCopy[index].paintColorId
            : defaultPaintColorId
          : null;
        partsListDeepCopy[index].partNumberIsInvalid = false;
        partsListDeepCopy[index].invalidPartNumberMessage = "";
        partsListDeepCopy[index].isFullTrim = false;
        partsListDeepCopy[index].impellerTrimValue =
          responseBody.isImpeller && partsListDeepCopy[index].impellerTrimValue === null
            ? calculateTrimOptionAndValue("")[0]
            : calculateTrimOptionAndValue(partsListDeepCopy[index].impellerTrimValue)[0];
        partsListDeepCopy[index].impellerTrimOption = calculateTrimOptionAndValue(
          partsListDeepCopy[index].impellerTrimValue
        )[0];
        partsListDeepCopy[index].impellerAngle =
          partsListDeepCopy[index].impellerAngle !== null ? partsListDeepCopy[index].impellerAngle : null;
        setPartFound(true);
        setErrorMessage("");
        setSearchComplete(true);
        setPartsList(partsListDeepCopy);
        setLoading(false);
      } else {
        setPartFound(false);
        partsListDeepCopy[index].partNumberIsInvalid = true;
        partsListDeepCopy[index].invalidPartNumberMessage =
          "Part number invalid. Please check the part number and try again.";
        setPartsList(partsListDeepCopy);
        setSearchComplete(true);
        setLoading(false);
      }
    } else {
      setPartFound(false);
      partsListDeepCopy[index].partNumberIsInvalid = true;
      partsListDeepCopy[index].invalidPartNumberMessage =
        "Part number invalid. Please check the part number and try again.";
      setPartsList(partsListDeepCopy);
      setSearchComplete(true);
      setLoading(false);
    }
    setLoading(false);
  }

  // Validate quantity input.
  function validateQuantity(quantity: string) {
    if (
      (parseInt(quantity, 10) > 0 || quantity === "") &&
      parseInt(quantity, 10) <= MAX_ITEM_QUANTITY &&
      quantity.indexOf(".") === -1 &&
      stringIsInteger(quantity)
    ) {
      return true;
    } else {
      return false;
    }
  }

  // Change quantity of part.
  function handleQuantityChange(index: number, newQuantity: string) {
    const partsListDeepCopy = deepCopy(partsList);
    if (validateQuantity(newQuantity)) {
      partsListDeepCopy[index].quantity = newQuantity;
    } else {
      partsListDeepCopy[index].quantity = "";
    }
    setPartsList(partsListDeepCopy);
  }

  // Change angle of impeller.
  function handleImpellerAngleChange(index: number, angle: string | null) {
    const partsListDeepCopy = deepCopy(partsList);
    if (angle !== null) {
      if (angle === "" || stringIsValidNumber(angle)) {
        partsListDeepCopy[index].impellerAngle = angle;
      }
    } else {
      partsListDeepCopy[index].impellerAngle = null;
    }
    setPartsList(partsListDeepCopy);
  }

  // Change trim selection option of impeller.
  function handleImpellerTrimOptionChange(index: number, trimOption: string) {
    const partsListDeepCopy = deepCopy(partsList);
    switch (trimOption) {
      case "full-trim":
        partsListDeepCopy[index].isFullTrim = true;
        partsListDeepCopy[index].impellerTrimOption = "full-trim";
        partsListDeepCopy[index].impellerTrimValue = "0.0";
        break;
      case "custom":
        partsListDeepCopy[index].isFullTrim = false;
        partsListDeepCopy[index].impellerTrimOption = "custom";
        partsListDeepCopy[index].impellerTrimValue = "0.0";
        break;
      default:
        partsListDeepCopy[index].isFullTrim = false;
        partsListDeepCopy[index].impellerTrimOption = "";
        partsListDeepCopy[index].impellerTrimValue = "";
        break;
    }
    setPartsList(partsListDeepCopy);
  }

  // Change trim value of impeller.
  function handleImpellerTrimValueChange(index: number, trimValue: string) {
    const partsListDeepCopy = deepCopy(partsList);
    if (trimValue === "" || stringIsValidNumber(trimValue)) {
      partsListDeepCopy[index].impellerTrimValue = trimValue;
    }
    setPartsList(partsListDeepCopy);
  }

  // Validate Quantity.
  function validateItemQuantity(quantity: string): boolean {
    if (parseInt(quantity, 10) > MAX_ITEM_QUANTITY || parseInt(quantity, 10) <= 0 || !stringIsValidNumber(quantity)) {
      return false;
    } else {
      return true;
    }
  }

  // Validate impeller angle.
  function validateImpellerAngle(angle: string | null): boolean {
    if (
      (angle !== null && isNaN(parseFloat(angle))) ||
      Number(angle) < 0 ||
      Number(angle) > MAX_IMPELLER_ANGLE ||
      (angle !== null && angle === "") ||
      angle === "" ||
      angle === "."
    ) {
      return false;
    } else {
      return true;
    }
  }

  // Validate impeller trim.
  function validateImpellerTrim(trim: string | null): boolean {
    if (
      (trim !== null && isNaN(parseFloat(trim))) ||
      Number(trim) < 0 ||
      Number(trim) > MAX_IMPELLER_TRIM ||
      (trim !== null && trim === "") ||
      trim === "" ||
      trim === "."
    ) {
      return false;
    } else {
      return true;
    }
  }

  function validatePaintColor(paintColorId: number | null): boolean {
    const foundColor = paintColors.find((color) => color.paintColorId === paintColorId);
    if ((paintColorId !== null && isNaN(paintColorId)) || foundColor === undefined) {
      return false;
    } else {
      return true;
    }
  }

  // Validate part.
  function partIsValid(
    quantity: string,
    isImpeller: boolean,
    angle: string | null,
    trim: string | null
  ): [errorMessage: string, validPart: boolean] {
    if (!validateItemQuantity(quantity)) {
      return [`Quantity must be a number between 1 and ${MAX_ITEM_QUANTITY}.`, false];
    } else if (isImpeller && !validateImpellerAngle(angle)) {
      return [`Impeller angle must be between 0 and ${MAX_IMPELLER_ANGLE} degrees.`, false];
    } else if (isImpeller && !validateImpellerTrim(trim)) {
      return [`Impeller trim must be between 0 and ${MAX_IMPELLER_TRIM} inches.`, false];
    } else if (isImpeller && angle === null && trim === null) {
      return ["Impeller Angle and Trim must be entered.", false];
    } else if (isImpeller && trim !== null && angle === null) {
      return ["Impeller Angle must be entered.", false];
    } else if (isImpeller && angle !== null && trim === null) {
      return [
        `Impeller trim must be between 0 and ${MAX_IMPELLER_TRIM} inches, or 'Full Trim' must be selected.`,
        false,
      ];
    } else {
      return ["", true];
    }
  }

  // Add parts to cart.
  async function handleAddToCart(partsList: Part[]): Promise<void> {
    // Check if all parts are valid.
    let allPartsValid = true;
    for (const part of partsList) {
      const [errorMessage, validPart] = partIsValid(
        part.quantity,
        part.isImpeller,
        part.impellerAngle,
        part.impellerTrimValue
      );
      if (!validPart) {
        allPartsValid = false;
        setErrorMessage(errorMessage);
        break;
      }
    }

    if (allPartsValid) {
      // Construct the item list to be used in the request body for adding items to the cart.
      const cartItems: CartItem[] = [];
      partsList.forEach((part) => {
        cartItems.push({
          partId: part.partId,
          paintColorId: part.isPaintable ? part.paintColorId : null,
          quantity: parseInt(part.quantity, 10),
          impellerTrim: part.isImpeller
            ? part.impellerTrimValue !== null && part.impellerTrimOption.toLowerCase() === "full trim"
              ? "0.0"
              : part.impellerTrimValue
            : null,
          impellerAngle: part.isImpeller ? part.impellerAngle : null,
          units: part.unitName,
        });
      });

      const requestBody = {
        replaceAll: false,
        items: cartItems,
      };

      setLoading(true);
      const [response] = await apiRequest(`${API}/cart`, "PUT", requestBody);
      if (response.ok) {
        setErrorMessage("");
        setCartQuantityCounter((previous) => previous + 1);
        setPartsList([]);
        partsList.length > 1
          ? setSuccessMessage("All items have been added to shopping cart.")
          : setSuccessMessage(`${partsList[0].quantity} ${partsList[0].description} added to shopping cart.`);
      } else {
        setErrorMessage("Error adding parts to cart. Please verify all part information is valid and try again.");
        setLoading(false);
      }
      setLoading(false);
    }
  }

  return (
    <Fragment>
      <div className="container-fluid w-75 justify-content-center p-4 m-auto">
        <div className={`${styles.body} card mt-4 mb-4`}>
          <div data-test="quick-order-page-card" className="card-body justify-content-center">
            {/* Page Title */}
            <h5 data-test="quick-order-page-title" className="card-title">
              <div className={`row text-center ${styles.quickOrderPageTitle}`}>
                <p>Quick Order</p>
              </div>
            </h5>

            {/* Quick Order Parts List */}
            <QuickOrderList
              loading={loading}
              partFound={partFound}
              errorMessage={errorMessage}
              searchComplete={searchComplete}
              inputValue={inputValue}
              paintColors={paintColors}
              partsList={partsList}
              onDeletePart={(lineItemNumber) => handleDeletePart(lineItemNumber)}
              onSearchComplete={(complete) => setSearchComplete(complete)}
              onErrorMessageChange={(errorMessage) => setErrorMessage(errorMessage)}
              onLoadingChange={(loading) => setLoading(loading)}
              onInputValueChange={(inputValue) => setInputValue(inputValue)}
              onPartNumberChange={(index, newPartNumber) => handlePartNumberChange(index, newPartNumber)}
              onPartNumberSearch={(index, partNumber) => performPartNumberSearch(index, partNumber)}
              onQuantityChange={(index, quantity) => handleQuantityChange(index, quantity)}
              onPaintColorChange={(index, colorId) => handlePaintColorChange(index, colorId)}
              onTrimValueChange={(index, trimValue) => handleImpellerTrimValueChange(index, trimValue)}
              onTrimOptionChange={(index, trimOption) => handleImpellerTrimOptionChange(index, trimOption)}
              onAngleChange={(index, angle) => handleImpellerAngleChange(index, angle)}
              onPerformSearch={() => performSearch()}
            />

            {/* Quick Order Part Search Form */}
            <QuickOrderForm
              loading={loading}
              errorMessage={errorMessage}
              inputValue={inputValue}
              searchComplete={searchComplete}
              partFound={partFound}
              onErrorMessageChange={(errorMessage) => setErrorMessage(errorMessage)}
              onLoadingChange={(loading) => setLoading(loading)}
              onInputValueChange={(inputValue) => setInputValue(inputValue)}
              onSearchComplete={(complete) => setSearchComplete(complete)}
              onPerformSearch={() => performSearch()}
            />

            {/* Quick Order Page Buttons */}
            <div className="row mx-1 mt-2 py-3 px-2 justify-content-center">
              {/* Add to Cart */}
              <button
                data-test="quick-order-page-add-to-cart-button"
                type="submit"
                className="col col-lg-4 btn btn-primary w-25 mx-2"
                disabled={partsList.length === 0}
                onClick={() => handleAddToCart(partsList)}
              >
                Add To Cart
              </button>

              {/* Import Csv */}
              <ImportCsvButton
                buttonText="Import CSV"
                defaultPaintColorId={defaultPaintColorId}
                paintColors={paintColors}
                onUploadedPartsListChange={(uploadedPartsList) => {
                  processUploadedParts(uploadedPartsList);
                }}
                onErrorMessageChange={(errorMessage) => setErrorMessage(errorMessage)}
              />
              <div className="row pt-2 mx-5 justify-content-end">
                <span className="figure-caption col col-lg-6 text-start">
                  <ImportCsvInfoModal
                    showModal={showHelpModal}
                    data-test="quick-order-page-import-csv-help-modal"
                    linkMessage="How do I import a CSV file?"
                    title="How to Import a CSV File"
                    darkBackground={true}
                    onClick={() => setShowHelpModal(true)}
                    onClose={() => setShowHelpModal(false)}
                  >
                    <HelpImportCsv />
                  </ImportCsvInfoModal>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Error Message Toast */}
      <Toast
        title="Error"
        message={errorMessage}
        show={errorMessage.length > 0}
        onClose={() => {
          setInputValue(""), setErrorMessage("");
        }}
        type="error"
        verticalPosition="top"
        data-test="quick-order-form-error-toast"
      />

      {/* Success Message Toast */}
      <Toast
        title="Item(s) Added To Cart"
        message={successMessage}
        show={successMessage.length > 0}
        onClose={() => {
          navigate("/shopping-cart");
          setSuccessMessage("");
          setInputValue("");
          setErrorMessage("");
          setPartsList([]);
        }}
        type="success"
        verticalPosition="middle"
        data-test="quick-order-form-success-toast"
      />
    </Fragment>
  );
}

interface CompanyPreferenceResponseBody {
  defaultPaintColor: PaintColor | null;
}

interface PaintColorResponseBody {
  paintColors: PaintColor[];
}

interface PaintColor {
  paintColorId: number;
  hexCode: string;
  name: string;
}

interface ResponseBody {
  partId: number;
  partNumber: string;
  description: string;
  quantity: number;
  unitName: string;
  price: number;
  companyDiscount: number | null;
  weight: number | null;
  isSellable: boolean;
  isImpeller: boolean;
  isPaintable: boolean;
}

interface Part {
  partId: number;
  partNumber: string;
  description: string;
  quantity: string;
  unitName: string;
  price: number;
  companyDiscount: number | null;
  weight: number | null;
  paintColorId: number | null;
  partNumberIsInvalid: boolean;
  isSellable: boolean;
  isPaintable: boolean;
  isImpeller: boolean;
  isFullTrim: boolean;
  impellerTrimOption: string;
  impellerTrimValue: string | null;
  impellerAngle: string | null;
  invalidPartNumberMessage: string;
}

interface ProcessedPart {
  partId: number;
  partNumber: string;
  description: string;
  quantity: string;
  unitName: string;
  price: number;
  companyDiscount: number | null;
  weight: number | null;
  paintColorId: number | null;
  partNumberIsInvalid: boolean;
  isSellable: boolean;
  isPaintable: boolean;
  isImpeller: boolean;
  isFullTrim: boolean;
  impellerTrimOption: string;
  impellerTrimValue: string | null;
  impellerAngle: string | null;
  invalidPartNumberMessage: string;
}

interface CartItem {
  impellerTrim: string | null;
  impellerAngle: string | null;
  paintColorId: number | null;
  partId: number;
  quantity: number;
  units: string;
}
